import type {
  ColumnDef,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import isEqual from 'lodash/isEqual';
import {
  isValidElement,
  type ReactNode,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useUserTrialDetails } from '@revelio/auth';
import { FeatureFlag } from '@revelio/core';
import { ScreenerDimension, ScreenerSortCol } from '@revelio/data-access';
import { TableChart } from '@revelio/replots';
import { useDisclosure } from '@revelio/ui';

import { CustomiseColumnsButton } from '../../../../../shared/components/customise-columns-button';
import { useUnleashFlag } from '../../../../../shared/unleash-flags/use-unleash-flag';
import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { FilterId } from '../../../filters/types/filters';
import { mapFilterIdToScreenerDimension } from '../../../filters/utils/screener-dimension';
import BlurredCell from '../../BlurredCell';
import type { ScreenerTableData } from '../../DataProvider/ScreenerTableMultiMetricProvider';
import type { ScreenerTableRow } from '../../DataProvider/utils/transform-screener-table-data';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import { ColumnSelectModal } from './column-select-modal';
import { ScreenerEmptyState } from './ScreenerEmptyState';
import { ScreenerTablePopover } from './ScreenerTablePopover';
import styles from './screener-theme.module.css';
import {
  resolveInitialTableColumns,
  useTableColumnSync,
} from './useTableColumnSync';
import {
  getScreenerColumnDefs,
  type ScreenerColumnDef,
} from './utils/screener-columns';

const SCREENER_TABLE_PAGE_SIZE = 35;

const currencyColumns = [
  'salary',
  'last_funding_amount',
  'total_funding_amount',
  'valuation',
];

const SORT_KEY_TO_ENUM: Record<string, ScreenerSortCol> = {
  headcount: ScreenerSortCol.SortByHeadcount,
  attrition: ScreenerSortCol.SortByAttrition,
  growth: ScreenerSortCol.SortByGrowth,
  growth_yoy: ScreenerSortCol.SortByGrowthYoy,
  hiring: ScreenerSortCol.SortByHiring,
  inflow: ScreenerSortCol.SortByInflow,
  outflow: ScreenerSortCol.SortByOutflow,
  salary: ScreenerSortCol.SortBySalary,
  tenure: ScreenerSortCol.SortByTenure,
};

const noop = () => undefined;

const getDefaultScreenerColumns = (primaryEntity: string): string[] => [
  primaryEntity,
  'Headcount',
  'Growth Rate',
  'Salary',
  'Attrition Rate',
  'Hiring Rate',
  'Tenure',
];

const METRIC_FILTER_TO_COLUMN_HEADERS: Partial<Record<FilterId, string[]>> = {
  [FilterId.MULTI_HEADCOUNT]: ['Headcount'],
  [FilterId.MULTI_GROWTH_RATE]: ['Growth Rate'],
  [FilterId.MULTI_HIRING_RATE]: ['Hiring Rate'],
  [FilterId.MULTI_ATTRITION_RATE]: ['Attrition Rate'],
  [FilterId.MULTI_SALARY]: ['Salary'],
  [FilterId.MULTI_TENURE]: ['Tenure'],
  [FilterId.MULTI_INFLOW]: ['Inflow'],
  [FilterId.MULTI_OUTFLOW]: ['Outflow'],
};

const COLUMN_HEADER_TOOLTIPS: Record<string, string> = {
  'Growth Rate': 'The change in headcount year over year.',
  'Cumulative Growth':
    'The current hiring rate minus the current attrition rate.',
};

const COMPANY_INFO_FILTER_TO_COLUMN_HEADER: Partial<Record<FilterId, string>> =
  {
    [FilterId.PUBLIC]: 'Public Company',
    [FilterId.EXTERNALLY_OWNED]: 'Subsidiary Of',
    [FilterId.FOUNDER_GENDER]: 'Founder Gender',
    [FilterId.FOUNDER_ETHNICITY]: 'Founder Ethnicity',
    [FilterId.LAST_FUNDING_AMOUNT]: 'Last Funding Amount',
    [FilterId.LAST_FUNDING_YEAR]: 'Last Funding Year',
    [FilterId.TOTAL_FUNDING_AMOUNT]: 'Total Funding Amount',
    [FilterId.YEAR_FOUNDED]: 'Year Founded',
  };

const parseStoredStringArray = (value: string | null): string[] => {
  if (!value) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
};

const adjustDataForCurrency = (
  data: ScreenerTableRow[],
  exchangeRate: number
): ScreenerTableRow[] => {
  if (
    !exchangeRate ||
    exchangeRate === 1 ||
    exchangeRate <= 0 ||
    // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
    isNaN(exchangeRate) ||
    // biome-ignore lint/suspicious/noGlobalIsFinite: migrated from develop
    !isFinite(exchangeRate)
  ) {
    return data;
  }

  return data.map((entity) => {
    const adjusted: ScreenerTableRow = {};
    Object.entries(entity).forEach(([key, value]) => {
      adjusted[key] =
        currencyColumns.some((col) => key.startsWith(col)) &&
        typeof value === 'number'
          ? value * exchangeRate
          : value;
    });
    return adjusted;
  });
};

const getPrimaryDimension = (
  revelioPageId: RevelioPageId | undefined
): FilterId | undefined => {
  if (revelioPageId === RevelioPageId.COMPANY_SCREENER) {
    return FilterId.COMPANY;
  }
  if (revelioPageId === RevelioPageId.GEOGRAPHY_SCREENER) {
    return FilterId.GEOGRAPHY;
  }
  if (revelioPageId === RevelioPageId.ROLE_SCREENER) {
    return FilterId.ROLE;
  }
  return undefined;
};

export const ScreenerTable = ({ data }: { data: ScreenerTableData }) => {
  const { revelioPageId } = useTabContentConfig();
  const { isTrialUser } = useUserTrialDetails();
  const { code: currencyCode, exchangeRate } = useCurrencyPreference();
  const effectiveFilters = useEffectiveFilters();
  const { setPage, sorting, setSorting } = data;
  const pageRef = useRef(data.page);
  pageRef.current = data.page;

  const view =
    mapFilterIdToScreenerDimension(getPrimaryDimension(revelioPageId)) ??
    ScreenerDimension.Company;

  const sortingDisabledColumns = useMemo(() => {
    const primaryEntity = (() => {
      if (view === ScreenerDimension.Company) {
        return 'Company Name';
      }
      if (view === ScreenerDimension.Geography) {
        return 'Metro Area';
      }
      return 'Role';
    })();

    return [
      primaryEntity,
      'Description',
      'Email',
      'Founder Ethnicity',
      'Founder Gender',
      'Funding Stage',
      'HQ Location',
      'Keywords',
      'Last Funding Amount',
      'Last Funding Year',
      'LinkedIn URL',
      'Phone Number',
      'Public Company',
      'RCID',
      'RICS K10',
      'RICS K50',
      'RICS K400',
      'Subsidiary Of',
      'Subsidiary Of (RCID)',
      'Total Funding Amount',
      'Valuation',
      'Website',
      'Year Founded',
    ];
  }, [view]);
  const hasVisibleFilters = effectiveFilters.some((filter) => filter.isVisible);

  const currencyAdjustedData = adjustDataForCurrency(data.rows, exchangeRate);

  const isValuationEnabled = useUnleashFlag(
    FeatureFlag.CompanyDiscoveryValuationFilter
  );
  const allColumns = useMemo<ScreenerColumnDef[]>(() => {
    if (!currencyAdjustedData || currencyAdjustedData.length === 0) {
      return [];
    }
    const columns = getScreenerColumnDefs(
      currencyAdjustedData,
      view,
      currencyCode
    );
    if (!isValuationEnabled) {
      return columns.filter((col) => !col.accessorKey.startsWith('valuation'));
    }
    return columns;
  }, [currencyAdjustedData, view, currencyCode, isValuationEnabled]);
  const availableColumnHeaders = useMemo(
    () => Array.from(new Set(allColumns.map((column) => column.header))),
    [allColumns]
  );

  const primaryEntity = (() => {
    if (view === ScreenerDimension.Company) {
      return 'Company Name';
    }
    if (view === ScreenerDimension.Geography) {
      return 'Metro Area';
    }
    return 'Role';
  })();

  const defaultColumns = useMemo(
    () => getDefaultScreenerColumns(primaryEntity),
    [primaryEntity]
  );
  const resolvedDefaultColumns = useMemo(
    () =>
      defaultColumns.filter((header) =>
        availableColumnHeaders.includes(header)
      ),
    [defaultColumns, availableColumnHeaders]
  );
  const storageKey = `columns_${revelioPageId ?? 'custom_dash'}`;
  const deselectedStorageKey = `deselected_auto_columns_${revelioPageId ?? 'custom_dash'}`;
  const initialLocationSearch =
    typeof window === 'undefined' ? '' : window.location.search;

  const [savedCheckedColumns, setSavedCheckedColumns] = useState<string[]>(() =>
    resolveInitialTableColumns({
      locationSearch: initialLocationSearch,
      storageKey,
      defaultColumns: resolvedDefaultColumns,
      validColumnNames: availableColumnHeaders,
    })
  );
  const [userDeselectedColumns, setUserDeselectedColumns] = useState<string[]>(
    () =>
      typeof window === 'undefined'
        ? []
        : parseStoredStringArray(
            window.sessionStorage.getItem(deselectedStorageKey)
          )
  );
  const autoAddedColumnsRef = useRef<string[]>([]);
  const processedColumnsRef = useRef<Set<string>>(new Set());
  const savedCheckedColumnsRef = useRef(savedCheckedColumns);
  const previousEffectiveFiltersRef = useRef(effectiveFilters);

  savedCheckedColumnsRef.current = savedCheckedColumns;

  useTableColumnSync(
    savedCheckedColumns,
    setSavedCheckedColumns,
    storageKey,
    resolvedDefaultColumns,
    availableColumnHeaders,
    { skipInitialHydration: true }
  );

  const companyInfoSelectors = useMemo(
    () =>
      effectiveFilters.flatMap((filter) => {
        if (!filter.isVisible) {
          return [];
        }
        const header = COMPANY_INFO_FILTER_TO_COLUMN_HEADER[filter.id];
        return header ? [header] : [];
      }),
    [effectiveFilters]
  );

  const autoCandidateHeaders = useMemo(() => {
    const segmentedHeaders = Array.from(
      new Set(
        allColumns
          .filter((column) => column.meta.isSegmented)
          .map((column) => column.header)
      )
    );

    return [...companyInfoSelectors, ...segmentedHeaders].filter(
      (headerText, index, arr) =>
        arr.indexOf(headerText) === index &&
        availableColumnHeaders.includes(headerText) &&
        !userDeselectedColumns.includes(headerText)
    );
  }, [
    companyInfoSelectors,
    availableColumnHeaders,
    userDeselectedColumns,
    allColumns,
  ]);
  const resetCheckedColumns = useMemo(() => {
    const segmentedHeaders = Array.from(
      new Set(
        allColumns
          .filter((column) => column.meta.isSegmented)
          .map((column) => column.header)
      )
    );

    const activeFilterColumns = [
      ...companyInfoSelectors,
      ...segmentedHeaders,
    ].filter(
      (headerText, index, arr) =>
        arr.indexOf(headerText) === index &&
        availableColumnHeaders.includes(headerText)
    );
    const activeMetricColumns = effectiveFilters
      .flatMap((filter) => {
        if (!filter.isVisible) {
          return [];
        }
        const candidates = METRIC_FILTER_TO_COLUMN_HEADERS[filter.id];
        if (!candidates || candidates.length === 0) {
          return [];
        }
        return candidates.filter((headerText) =>
          availableColumnHeaders.includes(headerText)
        );
      })
      .filter((headerText, index, arr) => arr.indexOf(headerText) === index);

    return [
      ...resolvedDefaultColumns,
      ...activeFilterColumns,
      ...activeMetricColumns,
    ].filter((headerText, index, arr) => arr.indexOf(headerText) === index);
  }, [
    companyInfoSelectors,
    effectiveFilters,
    allColumns,
    availableColumnHeaders,
    resolvedDefaultColumns,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (userDeselectedColumns.length === 0) {
      window.sessionStorage.removeItem(deselectedStorageKey);
      return;
    }

    window.sessionStorage.setItem(
      deselectedStorageKey,
      JSON.stringify(userDeselectedColumns)
    );
  }, [userDeselectedColumns, deselectedStorageKey]);

  useEffect(() => {
    const staleAutoAddedColumns = autoAddedColumnsRef.current.filter(
      (col) => !autoCandidateHeaders.includes(col)
    );

    if (staleAutoAddedColumns.length > 0) {
      autoAddedColumnsRef.current = autoAddedColumnsRef.current.filter(
        (col) => !staleAutoAddedColumns.includes(col)
      );
    }

    if (allColumns.length === 0) return;

    const filtersChanged = !isEqual(
      previousEffectiveFiltersRef.current,
      effectiveFilters
    );
    if (filtersChanged) {
      processedColumnsRef.current.clear();
      previousEffectiveFiltersRef.current = effectiveFilters;
    }

    const columnsToAdd: string[] = [];
    const newAutoCandidates = autoCandidateHeaders.filter(
      (headerText) =>
        !processedColumnsRef.current.has(headerText) &&
        !savedCheckedColumnsRef.current.includes(headerText) &&
        !autoAddedColumnsRef.current.includes(headerText)
    );

    if (newAutoCandidates.length > 0) {
      // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
      newAutoCandidates.forEach((selector) =>
        processedColumnsRef.current.add(selector)
      );
      columnsToAdd.push(...newAutoCandidates);
    }

    const nextColumnsToAdd = columnsToAdd.filter(
      (col) =>
        !savedCheckedColumnsRef.current.includes(col) &&
        !autoAddedColumnsRef.current.includes(col)
    );

    if (nextColumnsToAdd.length > 0) {
      autoAddedColumnsRef.current = [
        ...autoAddedColumnsRef.current,
        ...nextColumnsToAdd,
      ];
    }
  }, [allColumns, autoCandidateHeaders, effectiveFilters]);

  const effectiveCheckedColumns = useMemo(
    () =>
      [...savedCheckedColumns, ...autoCandidateHeaders].filter(
        (headerText, index, arr) => arr.indexOf(headerText) === index
      ),
    [savedCheckedColumns, autoCandidateHeaders]
  );

  const filteredColumns =
    effectiveCheckedColumns.length === 0
      ? allColumns
      : allColumns.filter((column) =>
          effectiveCheckedColumns.includes(column.header)
        );

  const handleHeaderClick = useCallback(
    (columnId: string) => {
      const column = filteredColumns.find(
        (col) => String(col.accessorKey) === columnId
      );

      if (!column?.meta.sortKey || column.meta.columnNumber === undefined) {
        return;
      }

      const newSortBy = SORT_KEY_TO_ENUM[column.meta.sortKey];

      if (!newSortBy) return;

      const isSameColumn =
        sorting?.sort_by === newSortBy &&
        sorting?.num_col === column.meta.columnNumber;

      if (!isSameColumn) {
        setSorting({
          sort_by: newSortBy,
          num_col: column.meta.columnNumber,
          ascending: true,
        });
        return;
      }

      if (sorting?.ascending) {
        setSorting({
          sort_by: newSortBy,
          num_col: column.meta.columnNumber,
          ascending: false,
        });
        return;
      }

      setSorting(undefined);
    },
    [filteredColumns, sorting, setSorting]
  );
  const columnDefs: ColumnDef<ScreenerTableRow, unknown>[] = useMemo(() => {
    return filteredColumns.map((col): ColumnDef<ScreenerTableRow, unknown> => {
      const originalCell = col.cell;
      const columnKey = col.meta.columnKey;
      const isSortingDisabled = sortingDisabledColumns.some((disabledCol) =>
        col.header.includes(disabledCol)
      );

      return {
        id: String(col.accessorKey),
        accessorKey: col.accessorKey,
        header: col.header,
        meta: col.meta,
        enableSorting: !isSortingDisabled,
        cell: (info) => {
          const rowIndex = info.row.index;
          const globalRowIndex =
            (data.page - 1) * SCREENER_TABLE_PAGE_SIZE + rowIndex;
          const shouldBlur = isTrialUser && globalRowIndex >= 12;

          if (shouldBlur) {
            const value = info.getValue();
            const formattedValue = String(originalCell(info) ?? value ?? '');

            return (
              <div data-testid={`${columnKey}-cell`}>
                <BlurredCell
                  value={formattedValue}
                  onMouseEnter={noop}
                  allowUnblur={false}
                />
              </div>
            );
          }

          return originalCell(info);
        },
      };
    });
  }, [filteredColumns, data.page, isTrialUser, sortingDisabledColumns]);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleSaveColumns = (checkedColumns: string[]) => {
    setUserDeselectedColumns((prev) => {
      const nextDeselected = new Set(prev);
      const previousChecked = new Set(effectiveCheckedColumns);
      const nextChecked = new Set(checkedColumns);

      checkedColumns.forEach((column) => {
        nextDeselected.delete(column);
      });

      autoCandidateHeaders.forEach((column) => {
        const wasPreviouslyChecked = previousChecked.has(column);
        const isNowChecked = nextChecked.has(column);
        if (wasPreviouslyChecked && !isNowChecked) {
          nextDeselected.add(column);
        }
      });

      return Array.from(nextDeselected);
    });
    setSavedCheckedColumns(checkedColumns);
  };

  const shouldShowCustomiseColumnsButton =
    !!currencyAdjustedData?.length && data.rows.length > 0;

  const hasData = data.totalCount > 0 && currencyAdjustedData.length > 0;

  const paginationState: PaginationState = {
    pageIndex: data.page - 1,
    pageSize: SCREENER_TABLE_PAGE_SIZE,
  };

  // Handle pagination changes from TableChart
  const handlePaginationChange = useCallback(
    (
      updater: PaginationState | ((old: PaginationState) => PaginationState)
    ) => {
      const currentState = {
        pageIndex: pageRef.current - 1,
        pageSize: SCREENER_TABLE_PAGE_SIZE,
      };
      const newState =
        typeof updater === 'function' ? updater(currentState) : updater;
      const newPage = newState.pageIndex + 1;

      if (newPage === pageRef.current) {
        return;
      }

      pageRef.current = newPage;

      startTransition(() => {
        setPage(newPage);
      });
    },
    [setPage]
  );

  const tanStackSortingState: SortingState = useMemo(() => {
    if (!sorting) return [];

    const column = filteredColumns.find((col) => {
      if (!col.meta.sortKey) return false;
      const colSortBy = SORT_KEY_TO_ENUM[col.meta.sortKey];
      return (
        colSortBy === sorting.sort_by &&
        col.meta.columnNumber === sorting.num_col
      );
    });

    if (!column || !column.accessorKey) return [];

    return [
      {
        id: String(column.accessorKey),
        desc: !sorting.ascending,
      },
    ];
  }, [sorting, filteredColumns]);
  const onRenderRow = useCallback(
    ({
      rowContent,
      rowData,
      rowIndex,
    }: {
      rowContent: ReactNode;
      rowData: ScreenerTableRow;
      rowIndex: number;
    }) => {
      const globalRowIndex =
        (data.page - 1) * SCREENER_TABLE_PAGE_SIZE + rowIndex;
      const shouldDisableHover = isTrialUser && globalRowIndex >= 12;

      const rowKey = `screener-row-${rowIndex}`;

      if (!isValidElement(rowContent)) {
        return rowContent;
      }

      const originalClassName = rowContent.props?.className || '';
      const originalStyle = rowContent.props?.style || {};
      const children = rowContent.props?.children;

      if (view === ScreenerDimension.Company && !shouldDisableHover) {
        return (
          <ScreenerTablePopover data={rowData} key={rowKey}>
            <tr
              className={originalClassName}
              style={{ ...originalStyle, cursor: 'pointer' }}
              key={`${rowKey}-tr`}
            >
              {children}
            </tr>
          </ScreenerTablePopover>
        );
      }

      return (
        <tr
          className={originalClassName}
          style={{ ...originalStyle, cursor: 'pointer' }}
          key={rowKey}
        >
          {children}
        </tr>
      );
    },
    [view, data.page, isTrialUser]
  );

  if (!hasVisibleFilters) {
    return <ScreenerEmptyState />;
  }

  return (
    <div className={styles.screenerThemed}>
      <div className={styles.customiseColumnsAnchor}>
        {shouldShowCustomiseColumnsButton && (
          <CustomiseColumnsButton
            onClick={onOpen}
            right={0}
            variant="compact"
            hideTopRightRadius
          />
        )}
      </div>
      <ColumnSelectModal
        isOpen={isOpen}
        onClose={onClose}
        columns={availableColumnHeaders.map((header) => ({
          value: header,
          label: header,
          tooltip: COLUMN_HEADER_TOOLTIPS[header],
        }))}
        setSavedCheckedColumns={handleSaveColumns}
        savedCheckedColumns={effectiveCheckedColumns}
        resetCheckedColumns={resetCheckedColumns}
      />
      <TableChart
        data={currencyAdjustedData}
        columns={columnDefs}
        showZebraStripe={false}
        onRenderRow={onRenderRow}
        isPaginated={hasData}
        serverSidePagination={{
          externalPaginationState: paginationState,
          onPaginationChange: handlePaginationChange,
          totalItems: data.totalCount,
          pageSizeOptions: [SCREENER_TABLE_PAGE_SIZE],
          hidePageSizeSelector: true,
        }}
        serverSideSorting={{
          externalSortingState: tanStackSortingState,
          onSortingChange: noop,
          onHeaderClick: handleHeaderClick,
        }}
      />
    </div>
  );
};
